import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProjectCompatibilityRole, ProjectCompatibilityView, ProjectListItem } from '@nightshift/ui-types';

import { Kicker } from '../components/primitives.tsx';
import { registerUnsavedEditor } from '../lib/unsaved.ts';
import { announceSettingsSaved } from '../lib/settings-autosave.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { canWriteSettings } from '../lib/privilege.ts';
import { useRoute } from '../lib/router.ts';

export function ProjectSettings({ user }: { user: SessionUser | null }) {
  return (
    <>
      <AddProjectPanel user={user} />
      <ProjectRecordPanel user={user} />
      <ProjectCompatibilityPanel />
    </>
  );
}

const ROLE_LABEL: Record<ProjectCompatibilityRole['role'], string> = {
  refiner: 'Refiner',
  implementer: 'Implementer',
  reviewer: 'Reviewer',
};

/**
 * Which machines may take this project's work, per role, and why the others may not. Read from
 * the same evaluator the scheduler uses, so a machine shown as eligible here is one the queue
 * will bind. A project with no environment requirements says so rather than pretending every
 * machine was checked.
 */
function ProjectCompatibilityPanel() {
  const state = useLive();
  const slug = state.projectSlug;
  const [view, setView] = useState<ProjectCompatibilityView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setError(null);
    if (!slug) return;
    void (async () => {
      try {
        const listed = await api.projects();
        const match = listed.projects.find((row) => row.slug === slug);
        if (!match) return;
        const loaded = await api.projectCompatibility(match.id);
        if (!cancelled) setView(loaded);
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : String(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!slug) return null;
  return (
    <section className="sheet-panel" data-panel="compatibility">
      <Kicker>Environment</Kicker>
      {error ? <p className="bad">{error}</p> : null}
      {view && !view.declared ? (
        <p className="dim">
          No environment requirements are declared for this project, so any live machine with the configured harnesses may take
          its work. Declare build and run hosts, required toolchains and whether runs need a graphics session under Project →
          Execution to have machines checked before dispatch.
        </p>
      ) : null}
      {view?.declared ? (
        <>
          <p className="dim">
            Declared in the project configuration
            {view.shell ? ` · shell ${view.shell}` : ''}
            {view.workingDir ? ` · commands run in ${view.workingDir}` : ''}
            {view.commands.build ? ` · build: ${view.commands.build}` : ''}
            {view.commands.test ? ` · test: ${view.commands.test}` : ''}
          </p>
          {view.roles.map((role) => (
            <div key={role.role} className="compat-role" data-role={role.role}>
              <h4 className="compat-role-title">
                {ROLE_LABEL[role.role]}
                <span className="dim">
                  {' · '}
                  {role.constrains
                    ? [
                        role.hosts.length > 0 ? `hosts ${role.hosts.join(', ')}` : null,
                        role.tools.length > 0 ? `tools ${role.tools.join(', ')}` : null,
                        role.graphicsSession ? 'graphics session' : null,
                        role.inputSession ? 'input session' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : 'no machine requirements'}
                </span>
              </h4>
              {role.constrains ? (
                <table className="compat-table">
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Host</th>
                      <th>Verdict</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {role.machines.map((row) => (
                      <tr key={row.machineId} data-compatible={row.compatible ? 'yes' : 'no'}>
                        <td>
                          {row.machine}
                          {row.live ? '' : <span className="dim"> (offline)</span>}
                        </td>
                        <td>{row.host}</td>
                        <td>{row.compatible ? 'Eligible' : 'Ineligible'}</td>
                        <td className="compat-why">
                          {row.compatible
                            ? row.matched.map((tool) => `${tool.id} ${tool.versions[0] ?? ''}`.trim()).join(', ') || 'requirements match'
                            : row.reasons.map((reason) => reason.detail).join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          ))}
        </>
      ) : null}
    </section>
  );
}

function AddProjectPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const { replace } = useRoute();
  const canWrite = canWriteSettings(user);
  const [slug, setSlug] = useState('');
  const [tracker, setTracker] = useState('');
  const [repo, setRepo] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const result = await api.createProject({ slug, tracker, repo });
      const message =
        result.action === 'created'
          ? `Registered ${result.project.slug}.`
          : `Updated ${result.project.slug}.`;
      store.toast('good', message);
      if (result.webhook) {
        store.toast('good', `GitLab webhook ${result.webhook.action} on ${result.project.gitRepoPath}.`);
      }
      for (const warning of result.warnings) store.toast('info', warning);
      setSlug('');
      setTracker('');
      setRepo('');
      store.setProject(result.project.slug);
      replace({ project: result.project.slug, group: 'project' });
      await store.refetch();
    } catch (caught) {
      store.toast('bad', caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sheet-panel add-project-panel">
      <header className="panel-head">
        <Kicker>Add project</Kicker>
      </header>
      <div className="sheet-panel-body">
        <p className="cfg-help" style={{ marginTop: 0 }}>
          Registers the GitLab repository and configures its webhook when available.
          Work uses the fleet through the execution routes configured for each stage.
          Configure those routes under Project → Execution after registering.
          {!canWrite ? ' Only an admin can register a project.' : ''}
        </p>
        <div className="cfg-grid">
          <label className="cfg-field">
            <span className="cfg-label">Slug</span>
            <input
              className="input"
              value={slug}
              disabled={busy || !canWrite}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="sample-game"
              autoComplete="off"
            />
          </label>
          <label className="cfg-field">
            <span className="cfg-label">Ticket prefix</span>
            <input
              className="input"
              value={tracker}
              disabled={busy || !canWrite}
              onChange={(e) => setTracker(e.target.value)}
              placeholder="GAME"
              autoComplete="off"
            />
          </label>
          <label className="cfg-field">
            <span className="cfg-label">GitLab path</span>
            <input
              className="input"
              value={repo}
              disabled={busy || !canWrite}
              onChange={(e) => setRepo(e.target.value)}
              placeholder="studio/sample-game"
              autoComplete="off"
            />
          </label>
        </div>
        <p className="cfg-help">
          New tickets mint keys from this prefix ({tracker.trim() || 'KEY'}-1, {tracker.trim() || 'KEY'}-2, …) unless you type a key.
          The GitLab token on this orchestrator must be able to read the project.
        </p>
        <button
          type="button"
          className="btn btn-accent btn-sm"
          disabled={!canWrite || busy || !slug.trim() || !tracker.trim() || !repo.trim()}
          onClick={() => void submit()}
        >
          {busy ? 'Registering…' : 'Register project'}
        </button>
      </div>
    </section>
  );
}

function ProjectRecordPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const { replace } = useRoute();
  const state = useLive();
  const canWrite = canWriteSettings(user);
  const slug = state.projectSlug;
  const [project, setProject] = useState<ProjectListItem | null>(null);
  const [name, setName] = useState('');
  const [nextSlug, setNextSlug] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('');
  const [gitRepoPath, setGitRepoPath] = useState('');
  const [gitRepoId, setGitRepoId] = useState('');
  const [deployJob, setDeployJob] = useState('');
  const [deployEnvironment, setDeployEnvironment] = useState('production');
  const [deployHealthUrl, setDeployHealthUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirtyRef = useRef(false);
  const slugRef = useRef(slug);

  const load = useCallback(async () => {
    if (!slug) {
      setProject(null);
      return;
    }
    const requested = slug;
    try {
      const listed = await api.projects();
      if (requested !== slugRef.current) return;
      const match = listed.projects.find((row) => row.slug === requested) ?? null;
      setProject(match);
      setError(null);
      if (match && !dirtyRef.current) {
        setName(match.name);
        setNextSlug(match.slug);
        setDefaultBranch(match.defaultBranch);
        setGitRepoPath(match.gitRepoPath);
        setGitRepoId(match.gitRepoId ?? '');
        setDeployJob(match.deploy?.job ?? '');
        setDeployEnvironment(match.deploy?.environment ?? 'production');
        setDeployHealthUrl(match.deploy?.healthUrl ?? '');
      }
    } catch (caught) {
      if (requested !== slugRef.current) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [slug]);

  const projectDirty = project
    ? name !== project.name ||
      nextSlug !== project.slug ||
      defaultBranch !== project.defaultBranch ||
      gitRepoPath !== project.gitRepoPath ||
      gitRepoId !== (project.gitRepoId ?? '')
    : false;
  const deployDirty = project
    ? deployJob !== (project.deploy?.job ?? '') ||
      deployEnvironment !== (project.deploy?.environment ?? 'production') ||
      deployHealthUrl !== (project.deploy?.healthUrl ?? '')
    : false;
  const dirty = projectDirty || deployDirty;
  dirtyRef.current = dirty;
  slugRef.current = slug;

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => registerUnsavedEditor({ isDirty: () => dirtyRef.current, what: 'the project record' }),
    [],
  );

  if (!slug) {
    return (
      <p className="sheet-note">Pick a project in the header to edit its database record.</p>
    );
  }

  if (!project) {
    return error ? <p className="sheet-note">{error}</p> : null;
  }

  const save = async () => {
    setBusy(true);
    try {
      const result = await api.updateProject(project.id, {
        name,
        slug: nextSlug,
        defaultBranch,
        gitRepoPath,
        gitRepoId: gitRepoId.trim() === '' ? null : gitRepoId,
      });
      announceSettingsSaved('Project settings saved');
      if (result.project.slug !== slug) {
        store.setProject(result.project.slug);
        replace({ project: result.project.slug, group: 'project' });
      }
      dirtyRef.current = false;
      setProject(result.project);
      setName(result.project.name);
      setNextSlug(result.project.slug);
      setDefaultBranch(result.project.defaultBranch);
      setGitRepoPath(result.project.gitRepoPath);
      setGitRepoId(result.project.gitRepoId ?? '');
      await store.refetch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const saveDeploy = async () => {
    setBusy(true);
    try {
      const saved = await api.saveDeployConfig(project.id, {
        job: deployJob,
        environment: deployEnvironment,
        healthUrl: deployHealthUrl,
      });
      setProject(saved.project);
      setDeployJob(saved.deploy?.job ?? '');
      setDeployEnvironment(saved.deploy?.environment ?? 'production');
      setDeployHealthUrl(saved.deploy?.healthUrl ?? '');
      dirtyRef.current = false;
      setError(null);
      announceSettingsSaved(deployJob.trim() ? 'Deploy settings saved' : 'Deploy disabled');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };
  const saveAll = async () => {
    if (projectDirty) await save();
    if (deployDirty) await saveDeploy();
  };

  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker>Project record</Kicker>
      </header>
      <div className="sheet-panel-body">
        <ProjectAutosaveTrigger enabled={canWrite && dirty && !busy} save={saveAll} />
        <p className="cfg-help" style={{ marginTop: 0 }}>
          These fields live in the database. Changing the slug rewrites the project URL; do that
          carefully. CI expectations are on verification profiles, not here.
          {canWrite ? null : ' Only an admin can change this record.'}
        </p>
        <div className="cfg-grid">
          <label className="cfg-field">
            <span className="cfg-label">Name</span>
            <input className="input" value={name} disabled={!canWrite || busy} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="cfg-field">
            <span className="cfg-label">Slug</span>
            <input
              className="input"
              value={nextSlug}
              disabled={!canWrite || busy}
              onChange={(e) => setNextSlug(e.target.value)}
            />
          </label>
          <label className="cfg-field">
            <span className="cfg-label">Default branch</span>
            <input
              className="input"
              value={defaultBranch}
              disabled={!canWrite || busy}
              onChange={(e) => setDefaultBranch(e.target.value)}
            />
          </label>
          <label className="cfg-field">
            <span className="cfg-label">Git repo path</span>
            <input
              className="input"
              value={gitRepoPath}
              disabled={!canWrite || busy}
              onChange={(e) => setGitRepoPath(e.target.value)}
            />
          </label>
          <label className="cfg-field">
            <span className="cfg-label">Git repo id</span>
            <input
              className="input"
              value={gitRepoId}
              disabled={!canWrite || busy}
              onChange={(e) => setGitRepoId(e.target.value)}
              placeholder="optional"
            />
          </label>
        </div>
        {error ? <p className="cfg-error" role="alert">Unsaved: {error}</p> : null}
        <>
            <p className="cfg-help">
              Deploy creates a default-branch pipeline that runs the current GitLab job
              against a Done ticket&apos;s merge SHA, so an older ticket is a rollback.
              Empty job name disables Deploy. Nightshift does not hold production
              credentials; GitLab CI runs the script.
              {!canWrite ? ' Only an admin can change deploy settings.' : ''}
            </p>
            <div className="cfg-grid">
              <label className="cfg-field">
                <span className="cfg-label">Deploy job</span>
                <input
                  className="input"
                  value={deployJob}
                  disabled={!canWrite || busy}
                  onChange={(e) => setDeployJob(e.target.value)}
                  placeholder="deploy:production"
                  autoComplete="off"
                />
              </label>
              <label className="cfg-field">
                <span className="cfg-label">Environment</span>
                <input
                  className="input"
                  value={deployEnvironment}
                  disabled={!canWrite || busy}
                  onChange={(e) => setDeployEnvironment(e.target.value)}
                  placeholder="production"
                  autoComplete="off"
                />
              </label>
              <label className="cfg-field">
                <span className="cfg-label">Health URL</span>
                <input
                  className="input"
                  value={deployHealthUrl}
                  disabled={!canWrite || busy}
                  onChange={(e) => setDeployHealthUrl(e.target.value)}
                  placeholder="https://nightshift.example/healthz"
                  autoComplete="off"
                />
              </label>
            </div>
        </>
      </div>
    </section>
  );
}

function ProjectAutosaveTrigger({ enabled, save }: { enabled: boolean; save: () => Promise<void> }) {
  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => void save(), 850);
    return () => clearTimeout(timer);
  }, [enabled, save]);
  return null;
}
